/**
 * DevResetButton Component
 *
 * Botao para resetar dados de teste no footer.
 * Visivel apenas em ambiente de desenvolvimento e para usuario SADMIN.
 *
 * @since 2026-01-26
 */

import { AlertTriangle as ExclamationTriangleIcon, RefreshCw as ArrowPathIcon } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import toast from '@/lib/toast';

interface ResetDataResponse {
  success: boolean;
  message: string;
  tablesClean?: number;
  recordsInserted?: number;
  defaultPassword?: string;
}

/**
 * Botao inline para resetar dados de desenvolvimento.
 *
 * Caracteristicas:
 * - So aparece em ambiente DEV
 * - So aparece para usuario SADMIN (id = 1)
 * - Mostra confirmacao antes de executar
 * - Exibe progresso durante execucao
 */
export function DevResetButton() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar se esta em ambiente de desenvolvimento
  const isDev = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development';

  // Verificar se e SADMIN (user_id = 1 ou username = 'sadmin')
  const isSadmin = user?.id === 1 || user?.username === 'sadmin';

  // Nao renderizar se nao for DEV ou nao for SADMIN
  if (!isDev || !isSadmin) {
    return null;
  }

  const handleReset = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post<ResetDataResponse>('/core/dev/reset-data');

      if (response.data.success) {
        toast.success(
          <div>
            <strong>Dados resetados com sucesso!</strong>
            <p className="text-sm">Senha padrao: {response.data.defaultPassword || 'Teste@123'}</p>
          </div>,
          { duration: 5000 }
        );

        // Recarregar a pagina apos reset
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(response.data.message || 'Erro ao resetar dados');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao resetar: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Botao inline no footer */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
        title="Resetar dados de teste (SADMIN)"
      >
        <ArrowPathIcon className="h-3.5 w-3.5" />
        <span>Reset</span>
      </button>

      {/* Modal de confirmacao */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-black/20 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Resetar Dados de Teste
              </h3>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Esta acao vai <strong>APAGAR TODOS OS DADOS</strong> do banco e inserir os dados de teste.
            </p>

            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Dados que serao inseridos:</strong>
              </p>
              <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 list-disc list-inside">
                <li>9 niveis hierarquicos</li>
                <li>8 areas de negocio</li>
                <li>5 organizacoes</li>
                <li>24 regioes</li>
                <li>9 segmentos/canais</li>
                <li>65+ pessoas</li>
              </ul>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                <strong>Senha padrao:</strong> Teste@123
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReset}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Resetando...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="h-4 w-4" />
                    Confirmar Reset
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DevResetButton;
